var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import "../types.js";
import { registerSystem, DEPTHS } from "../ComponentRegistry.js";
import { PhysicsSystem } from "./Physics.js";
import { scaleVector2d, angleOfVector2d } from "../../utils/vector.js";
import { C as ComponentCategory, d as CollisionCategoryEnum } from "../../enums-86f58524.js";
import RAPIER from "@dimforge/rapier2d-compat";
import { L as LocomotionSchema } from "../../Locomotion-379972de.js";
import { T as TransformSchema } from "../../Transform-9450b7b5.js";
import { C as CollisionsSchema } from "../../Collisions-9e9492fb.js";
import { R as RigidBoxSchema, a as RigidBoxResolverSchema } from "../../RigidBox-423b8c11.js";
import "../../decorators/type.js";
import "ajv";
import "../../utils/clone.js";
import "bitecs";
import "../../utils/typehelpers.js";
import "../../global-33d5eebf.js";
import "../../utils/byteArray.js";
import "../entity/Types.js";
import "../../console/flags.js";
import "../../persist/persist.js";
import "../../console/editor.js";
import "../../console/floatingwindow.js";
import "lodash";
import "../../Description-c36d0b9a.js";
import "../../Attach-abbbd93e.js";
import "../../utils/windowSessionStorage.js";
import "pixi.js";
import "../../FrameRate-7ce72a03.js";
import "../../Physics-8d447f7a.js";
class RigidBoxSystem {
  constructor() {
    __publicField(this, "type", "RigidBox");
    __publicField(this, "category", ComponentCategory.PHYSICS);
    __publicField(this, "schema", RigidBoxSchema);
    __publicField(this, "depth", DEPTHS.COLLISION - 1e-4);
    __publicField(this, "bodies", {});
  }
  init(entity, gameModel) {
    const rigidBox = gameModel.getTypedUnsafe(entity, RigidBoxSchema);
    const transformSchema = gameModel.getTypedUnsafe(entity, TransformSchema);
    const position = transformSchema.position;
    const physicsSystem = gameModel.getSystem(PhysicsSystem);
    const engine = physicsSystem.getEngine(gameModel);
    const prevBody = this.bodies[entity];
    if (prevBody) {
      engine.removeRigidBody(prevBody);
    }
    const rigidBodyDesc = rigidBox.isStatic ? RAPIER.RigidBodyDesc.fixed() : RAPIER.RigidBodyDesc.dynamic();
    rigidBodyDesc.setTranslation(position.x, position.y);
    const rigidBody = engine.createRigidBody(rigidBodyDesc);
    let colliderDesc = RAPIER.ColliderDesc.cuboid(rigidBox.width / 2, rigidBox.height / 2).setMass(rigidBox.mass);
    let filterMask = CollisionCategoryEnum.ALL;
    if (rigidBox.collisionMask) {
      filterMask = rigidBox.collisionMask.reduce((acc, val) => acc | val, 0);
    }
    const memberMask = rigidBox.collisionCategory << 16;
    colliderDesc.setCollisionGroups(memberMask | filterMask);
    if (rigidBox.isSensor) {
      colliderDesc = colliderDesc.setSensor(true);
    }
    if (rigidBox.collisionEvents) {
      colliderDesc.setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    }
    const collider = engine.createCollider(colliderDesc, rigidBody);
    collider.setTranslationWrtParent(rigidBox.point);
    physicsSystem.colliderHandleMap.entityToHandle[entity] = collider.handle;
    physicsSystem.colliderHandleMap.handleToEntity[collider.handle] = entity;
    this.bodies[entity] = rigidBody;
    if (!gameModel.hasComponent(entity, "RigidBoxResolver")) {
      gameModel.addComponent(entity, "RigidBoxResolver");
    }
  }
  runAll(gameModel) {
    const entities = gameModel.getComponentActives("RigidBox");
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const rigidBox = gameModel.getTypedUnsafe(entity, RigidBoxSchema);
      let body = this.bodies[entity];
      if (rigidBox.disabled) {
        if (body) {
          this.cleanup(entity, gameModel);
        }
        continue;
      } else {
        if (!body) {
          this.init(entity, gameModel);
          body = this.bodies[entity];
        }
      }
      const transformSchema = gameModel.getTypedUnsafe(entity, TransformSchema);
      const position = transformSchema.position;
      const locomotionSchema = gameModel.getTypedUnsafe(entity, LocomotionSchema);
      const velocity = locomotionSchema.velocity;
      body.setTranslation(position, true);
      if (velocity) {
        body.setLinvel(scaleVector2d(velocity, 60), true);
      }
      if (gameModel.hasComponent(entity, RigidBoxSchema) && gameModel.hasComponent(entity, LocomotionSchema)) {
        rigidBox.angle = angleOfVector2d({ x: locomotionSchema.directionX, y: locomotionSchema.directionY });
      }
      const rads = rigidBox.angle * (Math.PI / 180);
      if (rads !== body.rotation()) {
        body.setRotation(rads, true);
      }
    }
  }
  cleanup(entity, gameModel) {
    gameModel.removeComponent(entity, "RigidBoxResolver");
    const physicsSystem = gameModel.getSystem(PhysicsSystem);
    const engine = physicsSystem.getEngine(gameModel);
    const collisions = gameModel.getTypedUnsafe(gameModel.coreEntity, CollisionsSchema).collisionMap;
    if (collisions == null ? void 0 : collisions[entity]) {
      Object.keys(collisions[entity]).forEach((other) => {
        const otherKey = parseInt(other);
        if (collisions[otherKey]) {
          delete collisions[otherKey][entity];
        }
      });
      delete collisions[entity];
    }
    const handle = physicsSystem.colliderHandleMap.entityToHandle[entity];
    if (handle !== void 0) {
      const collider = engine.getCollider(handle);
      if (collider)
        engine.removeCollider(collider, false);
    }
    delete physicsSystem.colliderHandleMap.handleToEntity[handle];
    delete physicsSystem.colliderHandleMap.entityToHandle[entity];
    const body = this.bodies[entity];
    if (body) {
      try {
        engine.removeRigidBody(body);
      } catch (e) {
      }
      delete this.bodies[entity];
    }
  }
}
registerSystem(RigidBoxSystem);
class RigidBoxResolverSystem {
  constructor() {
    __publicField(this, "type", "RigidBoxResolver");
    __publicField(this, "category", ComponentCategory.PHYSICS);
    __publicField(this, "depth", DEPTHS.COLLISION + 1e-5);
    __publicField(this, "schema", RigidBoxResolverSchema);
  }
  runAll(gameModel) {
    const rigidBoxSystem = gameModel.getSystem(RigidBoxSystem);
    const entities = gameModel.getComponentActives("RigidBoxResolver");
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const box = rigidBoxSystem.bodies[entity];
      const rigidBox = gameModel.getTypedUnsafe(entity, RigidBoxSchema);
      if (!box)
        continue;
      const position = box.translation();
      const positionX = position.x - rigidBox.point.x;
      const positionY = position.y - rigidBox.point.y;
      if (rigidBox.point.x == 0 && rigidBox.point.y === 0) {
        const transformSchema = gameModel.getTypedUnsafe(entity, TransformSchema);
        transformSchema.x = positionX;
        transformSchema.y = positionY;
      }
      if (rigidBox.velocityLock) {
        const velocity = box.linvel();
        const locomotionSchema = gameModel.getTypedUnsafe(entity, LocomotionSchema);
        locomotionSchema.velocityX = velocity.x / 60 * (rigidBox.restitution || 1);
        locomotionSchema.velocityY = velocity.y / 60 * (rigidBox.restitution || 1);
      }
      if (rigidBox.directionLock) {
        const direction = box.rotation();
        const locomotionSchema = gameModel.getTypedUnsafe(entity, LocomotionSchema);
        locomotionSchema.directionX = Math.cos(direction);
        locomotionSchema.directionY = Math.sin(direction);
      }
    }
  }
}
registerSystem(RigidBoxResolverSystem);
export {
  RigidBoxSystem
};
//# sourceMappingURL=RigidBox.js.map
